/**
 * Grapple hook particle effects — launch, attach, arrival, scrape, trail.
 */

import { GRAPPLE_FX } from '../../../constants';
import type { ParticleCore } from '../types';

/** Hook launch — sparks from turret tip in aim direction */
export function spawnGrappleLaunch(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  const G = GRAPPLE_FX;

  // Bright white flash at tip
  core.spawnParticle(x, y, 0xffffff, 0, 0, 0.05, 0.08, 1.5, 2.5, 0, 1, true, true, 1);

  // Directional sparks in cone
  for (let i = 0; i < G.LAUNCH_SPARK_COUNT; i++) {
    const spread = (Math.random() - 0.5) * Math.PI / 4;
    const sparkAngle = angle + spread;
    const speed = 4 + Math.random() * 8;
    const sparkColor = Math.random() < 0.3 ? 0xffffff : color;
    core.spawnParticleAtAngle(
      x, y, sparkAngle, speed,
      sparkColor, 0.1 + Math.random() * 0.1,
      0.4 + Math.random() * 0.4,
      0.02, 0.96, true, true, 1, 2,
    );
  }

  // Dark debris for contrast (Sakurai: mix in darker elements)
  for (let i = 0; i < 3; i++) {
    const dAngle = angle + (Math.random() - 0.5) * Math.PI / 3;
    core.spawnParticleAtAngle(
      x, y, dAngle, 2 + Math.random() * 3,
      0x1a1a2e, 0.15 + Math.random() * 0.1,
      0.5 + Math.random() * 0.3,
      0.08, 0.94, false, true, 0.8,
    );
  }
}

/** Hook attach — impact burst at anchor point */
export function spawnGrappleAttach(core: ParticleCore, x: number, y: number, color: number): void {
  const G = GRAPPLE_FX;

  // Bright impact flash
  core.spawnParticle(x, y, 0xffffff, 0, 0, 0.06, 0.1, 2.0, 3.0, 0, 1, true, true, 1);

  // Radial sparks
  for (let i = 0; i < G.ATTACH_SPARK_COUNT; i++) {
    const angle = (i / G.ATTACH_SPARK_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const speed = 3 + Math.random() * 7;
    const sparkColor = Math.random() < 0.4 ? 0xffffff : color;
    core.spawnParticleAtAngle(
      x, y, angle, speed,
      sparkColor, 0.12 + Math.random() * 0.08,
      0.3 + Math.random() * 0.4,
      0.04, 0.95, true, true, 1, 1.5,
    );
  }

  // Terrain debris
  for (let i = 0; i < 4; i++) {
    const angle = Math.random() * Math.PI * 2;
    core.spawnParticleAtAngle(
      x, y, angle, 2 + Math.random() * 4,
      0x1a1a2e, 0.2 + Math.random() * 0.15,
      0.4 + Math.random() * 0.3,
      0.1, 0.93, false, true, 0.7,
    );
  }

  // Impact lines radiating from anchor (Sakurai: straight lines vs curves)
  for (let i = 0; i < 4; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8; // upper hemisphere
    const life = 0.1 + Math.random() * 0.05;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 3,
      outerRadius: 20 + Math.random() * 15,
      expandSpeed: 180 + Math.random() * 60,
      color: Math.random() < 0.3 ? 0xffffff : color,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // Shockwave
  core.spawnShockwave(x, y, color, 30, 180, 2, 0.2);
}

/** Anchor arrival — big launch burst */
export function spawnGrappleArrival(core: ParticleCore, x: number, y: number, color: number): void {
  const G = GRAPPLE_FX;

  // Big flash (Sakurai phase 1)
  core.spawnParticle(x, y, 0xffffff, 0, 0, 0.08, 0.12, 3.0, 4.0, 0, 1, true, true, 1);
  core.spawnParticle(x, y, color, 0, 0, 0.1, 0.15, 2.5, 3.5, 0, 1, true, true, 0.8);

  // Directional burst — upward bias for launch direction
  for (let i = 0; i < G.ARRIVAL_SPARK_COUNT; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.2;
    const speed = 5 + Math.random() * 12;
    const sparkColor = Math.random() < 0.3 ? 0xffffff : color;
    core.spawnParticleAtAngle(
      x, y, angle, speed,
      sparkColor, 0.15 + Math.random() * 0.1,
      0.4 + Math.random() * 0.5,
      0.06, 0.95, true, true, 1, 2,
    );
  }

  // Dark framing particles (Sakurai: contrast)
  for (let i = 0; i < 5; i++) {
    const angle = Math.random() * Math.PI * 2;
    core.spawnParticleAtAngle(
      x, y, angle, 2 + Math.random() * 5,
      0x0a0a19, 0.2 + Math.random() * 0.15,
      0.6 + Math.random() * 0.4,
      0.08, 0.92, false, true, 0.7,
    );
  }

  // Shockwave ring (Sakurai: anime impact convention)
  core.spawnShockwave(x, y, color, 40, 250, 3, 0.25);
  core.spawnShockwave(x, y, 0xffffff, 25, 200, 2, 0.15);

  // End flash — delayed (Sakurai phase 4: quick sudden disappearance)
  core.pendingBlasts.push({
    timestamp: Date.now() + 80,
    callback: () => {
      core.spawnParticle(x, y, 0xffffff, 0, 0, 0.04, 0.06, 2.0, 3.0, 0, 1, true, true, 0.8);
    },
  });
}

/** Rope-terrain scrape — micro sparks at contact point */
export function spawnRopeScrape(core: ParticleCore, x: number, y: number, color: number): void {
  const angle = Math.random() * Math.PI * 2;
  core.spawnParticleAtAngle(
    x, y, angle, 1 + Math.random() * 3,
    Math.random() < 0.5 ? color : 0xffffff,
    0.06 + Math.random() * 0.04,
    0.2 + Math.random() * 0.2,
    0.02, 0.97, true, true, 0.8,
  );
}

/** Hook travel trail — small trailing particles behind hook */
export function spawnGrappleTrail(core: ParticleCore, x: number, y: number, vx: number, vy: number, color: number): void {
  const trailAngle = Math.atan2(vy, vx) + Math.PI; // opposite of travel direction
  core.spawnParticleAtAngle(
    x, y,
    trailAngle + (Math.random() - 0.5) * 0.5,
    0.5 + Math.random() * 1.5,
    Math.random() < 0.3 ? 0xffffff : color,
    GRAPPLE_FX.TRAIL_LIFE,
    0.2 + Math.random() * 0.2,
    0, 0.98, true, true, 0.6, 2,
  );
}
